import React, { useState, useEffect, useMemo, useDeferredValue } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  MdSearch,
  MdRefresh,
  MdCalendarToday,
  MdBusiness,
  MdArrowUpward,
  MdArrowDownward,
  MdClose
} from "react-icons/md";
import ProgramsIcon from "../../assets/icons/ProgramsIcon";
import axios from "../../utils/api/axios";
import { Company } from "../../utils/api/endpoints/companies";
import {
  parseProgramDetails,
  ProgramAPIResponse
} from "../../utils/api/endpoints/programs/parsePrograms";

interface CompanyProgramsTableTabProps {
  companies?: Company[];
}

export type ProgramCategory = "continuous" | "one_off" | "legacy";
export type ProgramProgress = "completed" | "in_progress" | "upcoming" | "not_scheduled";

// Flattened, pre-computed item structure for optimal O(1) sort/filter operations
interface OptimizedProgramItem {
  id: number;
  title: string;
  titleLower: string;
  companyName: string;
  companyNameLower: string;
  companyLogo: string | null;
  companyId?: number;
  startDateDisplay: string | null;
  startDateTimestamp: number;
  endDateDisplay: string | null;
  endDateTimestamp: number;
  category: ProgramCategory;
  categoryPriority: number; // 1: continuous, 2: one_off, 3: legacy
  progress: ProgramProgress; // in_progress | upcoming | completed | not_scheduled
  progressPriority: number; // 1: in_progress, 2: upcoming, 3: completed, 4: not_scheduled
  isActivated: boolean;
  type?: string;
}

const CompanyProgramsTableTab: React.FC<CompanyProgramsTableTabProps> = ({
  companies = []
}) => {
  const navigate = useNavigate();

  // Data states
  const [items, setItems] = useState<OptimizedProgramItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState<string>("");
  const deferredSearch = useDeferredValue(searchQuery);

  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [progressFilter, setProgressFilter] = useState<string>("all"); // all | in_progress | upcoming | completed | not_scheduled
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [companyFilter, setCompanyFilter] = useState<string>("all");

  // Sort states - default: latest start date first, then continuous, etc.
  const [sortField, setSortField] = useState<string>("start_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // O(1) lookup map for companies
  const companyMap = useMemo(() => {
    const map = new Map<number, Company>();
    for (const c of companies) {
      map.set(c.company_id, c);
    }
    return map;
  }, [companies]);

  // Fetch all programs in a single batch, pre-resolving all values upfront
  const fetchPrograms = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/v2/programs", {
        params: { limit: 1000, page: 0 }
      });

      const rawPrograms: ProgramAPIResponse[] = response.data?.programs || [];
      const optimized: OptimizedProgramItem[] = [];
      const now = Date.now();

      for (const raw of rawPrograms) {
        if (!raw || !raw.program_title || raw.is_delete) continue;

        try {
          const parsed = parseProgramDetails(raw);
          if (!parsed) continue;

          // Resolve company name and logo
          let companyName = parsed.company_name || "";
          let companyLogo: string | null = null;
          const cid = parsed.companyId || parsed.asset?.company_id;

          if (cid && companyMap.has(cid)) {
            const comp = companyMap.get(cid)!;
            if (!companyName) companyName = comp.company_name;
            companyLogo = comp.company_logo || null;
          }
          if (!companyName) companyName = "Unknown Company";

          // Resolve Program Category (Schedule Type)
          const arch = parsed.architecture_version ?? 1;
          let category: ProgramCategory = "legacy";
          let categoryPriority = 3;

          if (arch >= 2) {
            if (parsed.scheduling_type === "continuous") {
              category = "continuous";
              categoryPriority = 1;
            } else {
              category = "one_off";
              categoryPriority = 2;
            }
          }

          // Resolve Timestamps & Formatted Dates
          let startDateTimestamp = 0;
          let startDateDisplay: string | null = null;
          if (parsed.expected_start_date) {
            const m = moment(parsed.expected_start_date);
            if (m.isValid()) {
              startDateTimestamp = m.valueOf();
              startDateDisplay = m.format("DD MMM YYYY");
            }
          }

          let endDateTimestamp = 0;
          let endDateDisplay: string | null = null;
          if (parsed.expected_end_date) {
            const m = moment(parsed.expected_end_date);
            if (m.isValid()) {
              endDateTimestamp = m.valueOf();
              endDateDisplay = m.format("DD MMM YYYY");
            }
          }

          // Robust Timeline / Progress Calculation:
          // 1. If program has no dates configured: "not_scheduled"
          // 2. If end date exists and is in the past: "completed"
          // 3. If start date exists and is in the future: "upcoming"
          // 4. Otherwise (currently within start and end window, or started with open end): "in_progress"
          let progress: ProgramProgress = "not_scheduled";
          let progressPriority = 4;

          if (startDateTimestamp === 0 && endDateTimestamp === 0) {
            progress = "not_scheduled";
            progressPriority = 4;
          } else if (endDateTimestamp > 0 && endDateTimestamp < now) {
            progress = "completed";
            progressPriority = 3;
          } else if (startDateTimestamp > 0 && startDateTimestamp > now) {
            progress = "upcoming";
            progressPriority = 2;
          } else {
            progress = "in_progress";
            progressPriority = 1;
          }

          optimized.push({
            id: parsed.id,
            title: parsed.title,
            titleLower: (parsed.title || "").toLowerCase(),
            companyName,
            companyNameLower: companyName.toLowerCase(),
            companyLogo,
            companyId: cid,
            startDateDisplay,
            startDateTimestamp,
            endDateDisplay,
            endDateTimestamp,
            category,
            categoryPriority,
            progress,
            progressPriority,
            isActivated: parsed.isActivated,
            type: parsed.type
          });
        } catch {
          // ignore invalid item
        }
      }

      setItems(optimized);
    } catch (err: any) {
      console.error("Error fetching programs list for admin tab:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load programs. Please click Refresh to try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, [companyMap]);

  // Reset to first page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [deferredSearch, categoryFilter, progressFilter, statusFilter, companyFilter]);

  // Quick stats computed in O(N)
  const stats = useMemo(() => {
    let active = 0;
    let continuous = 0;
    let oneOff = 0;
    let legacy = 0;
    let inProgress = 0;
    let upcoming = 0;
    let completed = 0;
    let notScheduled = 0;

    for (const item of items) {
      if (item.isActivated) active++;
      if (item.category === "continuous") continuous++;
      else if (item.category === "one_off") oneOff++;
      else if (item.category === "legacy") legacy++;

      if (item.progress === "in_progress") inProgress++;
      else if (item.progress === "upcoming") upcoming++;
      else if (item.progress === "completed") completed++;
      else if (item.progress === "not_scheduled") notScheduled++;
    }

    return {
      total: items.length,
      active,
      continuous,
      oneOff,
      legacy,
      inProgress,
      upcoming,
      completed,
      notScheduled
    };
  }, [items]);

  // High performance filter using pre-computed lowercase fields
  const filteredItems = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();

    return items.filter(item => {
      if (q) {
        if (!item.titleLower.includes(q) && !item.companyNameLower.includes(q)) {
          return false;
        }
      }
      if (categoryFilter !== "all" && item.category !== categoryFilter) {
        return false;
      }
      if (progressFilter !== "all" && item.progress !== progressFilter) {
        return false;
      }
      if (statusFilter === "active" && !item.isActivated) return false;
      if (statusFilter === "inactive" && item.isActivated) return false;
      if (companyFilter !== "all" && String(item.companyId) !== companyFilter) {
        return false;
      }
      return true;
    });
  }, [items, deferredSearch, categoryFilter, progressFilter, statusFilter, companyFilter]);

  // Ultra-fast numeric & string sorting
  const sortedItems = useMemo(() => {
    const arr = [...filteredItems];
    const isAsc = sortDirection === "asc";

    return arr.sort((a, b) => {
      switch (sortField) {
        case "start_date": {
          // Both have dates
          if (a.startDateTimestamp !== 0 && b.startDateTimestamp !== 0) {
            if (a.startDateTimestamp !== b.startDateTimestamp) {
              return isAsc
                ? a.startDateTimestamp - b.startDateTimestamp
                : b.startDateTimestamp - a.startDateTimestamp;
            }
          } else if (a.startDateTimestamp !== 0) {
            return isAsc ? 1 : -1;
          } else if (b.startDateTimestamp !== 0) {
            return isAsc ? -1 : 1;
          }

          // Tie-break: Category priority (Continuous > One-off > Legacy)
          if (a.categoryPriority !== b.categoryPriority) {
            return isAsc
              ? b.categoryPriority - a.categoryPriority
              : a.categoryPriority - b.categoryPriority;
          }

          // Final tie-break by ID desc
          return isAsc ? a.id - b.id : b.id - a.id;
        }

        case "end_date": {
          if (a.endDateTimestamp === 0 && b.endDateTimestamp !== 0) return 1;
          if (b.endDateTimestamp === 0 && a.endDateTimestamp !== 0) return -1;
          if (a.endDateTimestamp !== b.endDateTimestamp) {
            return isAsc
              ? a.endDateTimestamp - b.endDateTimestamp
              : b.endDateTimestamp - a.endDateTimestamp;
          }
          return 0;
        }

        case "company":
          return isAsc
            ? a.companyName.localeCompare(b.companyName)
            : b.companyName.localeCompare(a.companyName);

        case "title":
          return isAsc
            ? a.title.localeCompare(b.title)
            : b.title.localeCompare(a.title);

        case "category":
          return isAsc
            ? a.categoryPriority - b.categoryPriority
            : b.categoryPriority - a.categoryPriority;

        case "progress":
          return isAsc
            ? a.progressPriority - b.progressPriority
            : b.progressPriority - a.progressPriority;

        case "status": {
          const valA = a.isActivated ? 1 : 0;
          const valB = b.isActivated ? 1 : 0;
          return isAsc ? valA - valB : valB - valA;
        }

        default:
          return 0;
      }
    });
  }, [filteredItems, sortField, sortDirection]);

  // Paginated view
  const totalPages = Math.ceil(sortedItems.length / pageSize) || 1;
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedItems.slice(start, start + pageSize);
  }, [sortedItems, currentPage, pageSize]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <span className="ml-1 text-[10px] text-gray-300 opacity-60 group-hover:opacity-100">
          ↕
        </span>
      );
    }
    return sortDirection === "asc" ? (
      <MdArrowUpward className="ml-1 inline h-3 w-3 text-blue-600" />
    ) : (
      <MdArrowDownward className="ml-1 inline h-3 w-3 text-blue-600" />
    );
  };

  const hasActiveFilters =
    searchQuery !== "" ||
    categoryFilter !== "all" ||
    progressFilter !== "all" ||
    statusFilter !== "all" ||
    companyFilter !== "all";

  const clearAllFilters = () => {
    setSearchQuery("");
    setCategoryFilter("all");
    setProgressFilter("all");
    setStatusFilter("all");
    setCompanyFilter("all");
    setSortField("start_date");
    setSortDirection("desc");
  };

  return (
    <div className="space-y-3">
      {/* Compact Unified Control Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
          {/* Quick Filter Pill Switcher */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <button
              onClick={() => {
                setCategoryFilter("all");
                setProgressFilter("all");
                setStatusFilter("all");
              }}
              className={`rounded-md px-2.5 py-1 font-medium transition ${
                categoryFilter === "all" && progressFilter === "all" && statusFilter === "all"
                  ? "bg-gray-900 text-white shadow-xs"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              All <span className="opacity-75">({stats.total})</span>
            </button>
            <button
              onClick={() => {
                setProgressFilter(progressFilter === "in_progress" ? "all" : "in_progress");
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition ${
                progressFilter === "in_progress"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/50"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
              In Progress ({stats.inProgress})
            </button>
            <button
              onClick={() => {
                setProgressFilter(progressFilter === "upcoming" ? "all" : "upcoming");
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition ${
                progressFilter === "upcoming"
                  ? "bg-amber-600 text-white shadow-xs"
                  : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/50"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Upcoming ({stats.upcoming})
            </button>
            <button
              onClick={() => {
                setProgressFilter(progressFilter === "completed" ? "all" : "completed");
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition ${
                progressFilter === "completed"
                  ? "bg-emerald-600 text-white shadow-xs"
                  : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/50"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Completed ({stats.completed})
            </button>
            <button
              onClick={() => {
                setStatusFilter(statusFilter === "active" ? "all" : "active");
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition ${
                statusFilter === "active"
                  ? "bg-emerald-700 text-white shadow-xs"
                  : "bg-emerald-50/70 text-emerald-800 hover:bg-emerald-100 border border-emerald-200/50"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Active ({stats.active})
            </button>
            <button
              onClick={() => {
                setCategoryFilter(categoryFilter === "continuous" ? "all" : "continuous");
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition ${
                categoryFilter === "continuous"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200/50"
              }`}
            >
              Continuous ({stats.continuous})
            </button>
            <button
              onClick={() => {
                setCategoryFilter(categoryFilter === "one_off" ? "all" : "one_off");
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition ${
                categoryFilter === "one_off"
                  ? "bg-sky-600 text-white shadow-xs"
                  : "bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200/50"
              }`}
            >
              One-off ({stats.oneOff})
            </button>
            <button
              onClick={() => {
                setCategoryFilter(categoryFilter === "legacy" ? "all" : "legacy");
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition ${
                categoryFilter === "legacy"
                  ? "bg-gray-700 text-white shadow-xs"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              Legacy ({stats.legacy})
            </button>
          </div>

          {/* Refresh Button */}
          <div className="flex items-center gap-2 self-end sm:self-auto">
            <span className="text-[11px] text-gray-400">
              {sortedItems.length} matched
            </span>
            <button
              onClick={fetchPrograms}
              disabled={loading}
              title="Refresh programs"
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
            >
              <MdRefresh className={`h-3.5 w-3.5 ${loading ? "animate-spin text-blue-600" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Compact Single-Line Filter Row */}
        <div className="mt-2.5 flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
          {/* Search Bar */}
          <div className="relative min-w-[200px] flex-1">
            <MdSearch className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by program or company name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="h-8 w-full rounded-md border border-gray-200 bg-gray-50/50 py-1 pl-8 pr-7 text-xs text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <MdClose className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Program Type Filter */}
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="h-8 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">Type: All</option>
            <option value="continuous">Continuous</option>
            <option value="one_off">One-off</option>
            <option value="legacy">Legacy (v1)</option>
          </select>

          {/* Progress / Timeline State Filter */}
          <select
            value={progressFilter}
            onChange={e => setProgressFilter(e.target.value)}
            className="h-8 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">Timeline: All</option>
            <option value="in_progress">In Progress</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="not_scheduled">Not Scheduled</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="h-8 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">Status: All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Company Filter (if companies provided) */}
          {companies.length > 0 && (
            <select
              value={companyFilter}
              onChange={e => setCompanyFilter(e.target.value)}
              className="h-8 max-w-[170px] truncate rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Company: All</option>
              {companies.map(c => (
                <option key={c.company_id} value={String(c.company_id)}>
                  {c.company_name}
                </option>
              ))}
            </select>
          )}

          {/* Clear Button */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="h-8 rounded-md border border-dashed border-gray-300 px-2 text-xs font-medium text-gray-600 hover:border-gray-400 hover:text-gray-900"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <span>{error}</span>
          <button
            onClick={fetchPrograms}
            className="font-medium underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Compact Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
        {loading ? (
          <div className="p-4 space-y-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse py-1">
                <div className="h-6 w-6 rounded bg-gray-200 shrink-0" />
                <div className="h-3.5 w-32 rounded bg-gray-200" />
                <div className="h-3.5 flex-1 rounded bg-gray-200" />
                <div className="h-3.5 w-20 rounded bg-gray-200" />
                <div className="h-3.5 w-20 rounded bg-gray-200" />
                <div className="h-4 w-16 rounded bg-gray-200" />
                <div className="h-4 w-18 rounded bg-gray-200" />
                <div className="h-4 w-14 rounded-full bg-gray-200" />
              </div>
            ))}
          </div>
        ) : sortedItems.length === 0 ? (
          <div className="py-12 text-center">
            <ProgramsIcon className="mx-auto h-8 w-8 text-gray-300 stroke-current" />
            <p className="mt-2 text-xs font-semibold text-gray-700">
              {items.length === 0 ? "No programs found" : "No programs match your filters"}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="mt-2 text-xs font-medium text-blue-600 hover:underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left">
              <thead className="bg-gray-50/90 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  {/* Company Name */}
                  <th
                    scope="col"
                    onClick={() => handleSort("company")}
                    className="group cursor-pointer select-none px-3.5 py-2 hover:text-gray-800"
                  >
                    <div className="flex items-center">
                      Company Name
                      <SortIndicator field="company" />
                    </div>
                  </th>

                  {/* Program Title */}
                  <th
                    scope="col"
                    onClick={() => handleSort("title")}
                    className="group cursor-pointer select-none px-3.5 py-2 hover:text-gray-800"
                  >
                    <div className="flex items-center">
                      Program Title
                      <SortIndicator field="title" />
                    </div>
                  </th>

                  {/* Start Date */}
                  <th
                    scope="col"
                    onClick={() => handleSort("start_date")}
                    className="group cursor-pointer select-none px-3 py-2 hover:text-gray-800"
                  >
                    <div className="flex items-center">
                      Start Date
                      <SortIndicator field="start_date" />
                    </div>
                  </th>

                  {/* End Date */}
                  <th
                    scope="col"
                    onClick={() => handleSort("end_date")}
                    className="group cursor-pointer select-none px-3 py-2 hover:text-gray-800"
                  >
                    <div className="flex items-center">
                      End Date
                      <SortIndicator field="end_date" />
                    </div>
                  </th>

                  {/* Program Type */}
                  <th
                    scope="col"
                    onClick={() => handleSort("category")}
                    className="group cursor-pointer select-none px-3 py-2 hover:text-gray-800"
                  >
                    <div className="flex items-center">
                      Program Type
                      <SortIndicator field="category" />
                    </div>
                  </th>

                  {/* Timeline / Progress */}
                  <th
                    scope="col"
                    onClick={() => handleSort("progress")}
                    className="group cursor-pointer select-none px-3 py-2 hover:text-gray-800"
                  >
                    <div className="flex items-center">
                      Timeline
                      <SortIndicator field="progress" />
                    </div>
                  </th>

                  {/* Status */}
                  <th
                    scope="col"
                    onClick={() => handleSort("status")}
                    className="group cursor-pointer select-none px-3 py-2 hover:text-gray-800"
                  >
                    <div className="flex items-center">
                      Status
                      <SortIndicator field="status" />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 bg-white text-xs">
                {paginatedItems.map(item => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/dashboard/programs/${item.id}`)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(`/dashboard/programs/${item.id}`);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    className="group cursor-pointer transition-colors hover:bg-blue-50/50 focus:bg-blue-50/70 focus:outline-none"
                  >
                    {/* Company Name */}
                    <td className="whitespace-nowrap px-3.5 py-2.5">
                      <div className="flex items-center gap-2">
                        {item.companyLogo ? (
                          <img
                            src={item.companyLogo}
                            alt=""
                            className="h-5 w-5 rounded object-cover border border-gray-200 shrink-0"
                          />
                        ) : (
                          <div className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-blue-500 to-indigo-600 text-white shrink-0">
                            <MdBusiness className="h-3 w-3" />
                          </div>
                        )}
                        <span className="font-medium text-gray-900 truncate max-w-[180px]">
                          {item.companyName}
                        </span>
                      </div>
                    </td>

                    {/* Program Title */}
                    <td className="px-3.5 py-2.5">
                      <span className="font-semibold text-blue-600 group-hover:text-blue-800 group-hover:underline truncate block max-w-[320px]">
                        {item.title}
                      </span>
                    </td>

                    {/* Start Date */}
                    <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-gray-600">
                      {item.startDateDisplay ? (
                        <div className="flex items-center gap-1">
                          <MdCalendarToday className="h-3 w-3 text-gray-400 shrink-0" />
                          <span>{item.startDateDisplay}</span>
                        </div>
                      ) : (
                        <span className="text-gray-300 italic">—</span>
                      )}
                    </td>

                    {/* End Date */}
                    <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-gray-600">
                      {item.endDateDisplay ? (
                        <div className="flex items-center gap-1">
                          <MdCalendarToday className="h-3 w-3 text-gray-400 shrink-0" />
                          <span>{item.endDateDisplay}</span>
                        </div>
                      ) : (
                        <span className="text-gray-300 italic">—</span>
                      )}
                    </td>

                    {/* Program Type */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {item.category === "continuous" ? (
                        <span className="inline-flex items-center gap-1 rounded border border-purple-200 bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                          Continuous
                        </span>
                      ) : item.category === "one_off" ? (
                        <span className="inline-flex items-center rounded border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">
                          One-off
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                          Legacy
                        </span>
                      )}
                    </td>

                    {/* Timeline / Progress */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {item.progress === "completed" ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Completed
                        </span>
                      ) : item.progress === "upcoming" ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          Upcoming
                        </span>
                      ) : item.progress === "in_progress" ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                          In Progress
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                          Not Scheduled
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {item.isActivated ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                          Inactive
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Compact Pagination Bar */}
        {!loading && sortedItems.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50/50 px-3.5 py-2 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500">Rows:</span>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-6 rounded border border-gray-200 bg-white px-1.5 text-[11px] text-gray-700 focus:outline-none"
              >
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-[11px] text-gray-400 ml-1">
                {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, sortedItems.length)} of{" "}
                {sortedItems.length}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="h-6 rounded border border-gray-200 bg-white px-2 text-[11px] font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>
              <span className="px-1.5 text-[11px] font-medium text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className="h-6 rounded border border-gray-200 bg-white px-2 text-[11px] font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyProgramsTableTab;
